import { AvatarPromptFormat } from "@next-platform/packages-ts-types";
import { Text, TextArea, TextVariants } from "@next-platform/client-common-spicy-ui";
import { useTranslation } from "@next-platform/client-common-core";

import type { IPromptSectionProps } from "@/components/AvatarGeneratorModal/types";
import GenerateSuggestedFieldButton from "@/components/CharacterForm/GenerateSuggestedFieldButton";
import { Col, Row } from "@/components/customComponents";
import Trans from "@/components/Trans/Trans";

const SHORTCUT_VALUES = { shortcut: "Ctrl+Enter" } as const;
const SHORTCUT_COMPONENTS = {
  kbd: <kbd className="px-1.5 py-0.5 text-[11px] font-semibold bg-gray-8 border border-gray-9 rounded" />,
};

const renderHelperText = (promptFormat: AvatarPromptFormat) => {
  if (promptFormat === AvatarPromptFormat.Prose) {
    return <Trans i18nKey="common:modal.generateAvatar.prompt.helperText.prose" />;
  }
  return <Trans i18nKey="common:modal.generateAvatar.prompt.helperText.keywords" />;
};

export const PromptSection = ({
  prompt,
  isAiPromptEnabled,
  isRequiredFieldsFilled,
  activeGeneratingField,
  suggestionField,
  promptFormat,
  onPromptChange,
  onGenerateSuggestField,
  errorMessage,
}: IPromptSectionProps) => {
  const { t } = useTranslation();
  const placeholderText =
    promptFormat === AvatarPromptFormat.Prose
      ? t("common:modal.generateAvatar.prompt.placeholder.prose")
      : t("common:modal.generateAvatar.prompt.placeholder.keywords");

  return (
    <Col>
      <Row justify="between" className="mb-0.5">
        <Text variant={TextVariants.LABEL_LARGE_REGULAR} className="ml-[2px]">
          <Trans i18nKey="common:modal.generateAvatar.prompt.label" />
        </Text>
        {isAiPromptEnabled ? (
          <GenerateSuggestedFieldButton
            field={"image-prompt"}
            disabled={!isRequiredFieldsFilled}
            onButtonClick={onGenerateSuggestField}
            isLoading={activeGeneratingField === suggestionField}
          />
        ) : null}
      </Row>
      <TextArea
        value={prompt}
        autoFocus
        placeholder={placeholderText}
        aria-labelledby="describe-image"
        rows={3}
        maxRows={6}
        onChange={onPromptChange}
        className="w-full"
        error={!!errorMessage}
        errorMessage={errorMessage}
      />
      <div className="flex flex-col md:flex-row justify-between md:items-center gap-1 mt-0.5 ml-[2px]">
        <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-gray-11">
          {renderHelperText(promptFormat)}
        </Text>
        <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="hidden md:block text-gray-11 whitespace-nowrap">
          <Trans i18nKey="common:modal.generateAvatar.prompt.shortcut" values={SHORTCUT_VALUES} components={SHORTCUT_COMPONENTS} />
        </Text>
      </div>
    </Col>
  );
};
